import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ExternalLink, Flag, AlertTriangle, Ban, ShieldCheck, Search, TrendingUp, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PayoutsPanel from "@/components/locanest/PayoutsPanel";
import SupportTicketsPanel from "@/components/locanest/SupportTicketsPanel";
import DisputesPanel from "@/components/locanest/DisputesPanel";
import EmailLogPanel from "@/components/locanest/EmailLogPanel";
import AdminListingsPanel from "@/components/locanest/AdminListingsPanel";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function AdminPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [status, setStatus] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [usersQ, setUsersQ] = useState(null);
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (t === "disputes" || t === "payouts") setTimeout(() => document.getElementById(`admin-${t}`)?.scrollIntoView({ behavior: "smooth" }), 600);
  }, []);
  const [trend, setTrend] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  const LIMIT = 20;

  const load = () => {
    api.get(`/admin/verifications?status=${status}&limit=${LIMIT}&skip=${skip}`)
      .then(({ data }) => {
        // Backward compat: some tests may still expect a bare list
        if (Array.isArray(data)) { setItems(data); setTotal(data.length); }
        else { setItems(data.items || []); setTotal(data.total || 0); }
      })
      .catch(() => toast.error("Load failed"));
    api.get("/admin/analytics/signups").then(({ data }) => setAnalytics(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, skip]);
  useEffect(() => { setSkip(0); }, [status]);
  useEffect(() => {
    api.get(`/admin/analytics/signups/trend?days=${trendDays}`)
      .then(({ data }) => setTrend(data))
      .catch(() => {});
  }, [trendDays]);

  const decide = async (approve) => {
    if (!selected) return;
    try {
      await api.post(`/admin/verifications/${selected.id}/${approve ? "approve" : "reject"}`, { notes: note });
      toast.success(`Verification ${approve ? "approved" : "rejected"}`);
      setSelected(null); setNote(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Admin</div>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Owner verifications</h1>
      <p className="text-[#5C6B7A] mt-2">Review submitted documents and approve or reject.</p>

      {analytics && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="signup-analytics">
          <StatBox label="Tenants total" v={analytics.tenants_total} sub={`+${analytics.tenants_this_week} this week`} onClick={() => setUsersQ({ title: "All tenants", params: { role: "tenant" } })} testId="stat-tenants" />
          <StatBox label="Owners total" v={analytics.owners_total} sub={`+${analytics.owners_this_week} this week`} onClick={() => setUsersQ({ title: "All owners", params: { role: "owner" } })} testId="stat-owners" />
          <StatBox label="Signups today" v={analytics.tenants_today + analytics.owners_today} sub={`${analytics.tenants_today} tenants · ${analytics.owners_today} owners`} onClick={() => setUsersQ({ title: "Signed up today", params: { since: "today" } })} testId="stat-signups-today" />
          <StatBox label="Grandfathered" v={analytics.grandfathered_users} sub="Permanent free Access" onClick={() => setUsersQ({ title: "Grandfathered (free-period) users", params: { cohort: "free_period" } })} testId="stat-grandfathered" />
        </div>
      )}

      {trend && trend.series && (
        <div className="mt-4 bg-[#F0EBE1] border border-[#1A1F2C]/10 rounded-lg p-4" data-testid="signup-trend-chart">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-[#C85A32]" />
              <div className="font-display text-lg text-[#1A1F2C]">Signups trend</div>
              <span className="text-xs text-[#5C6B7A]">last {trend.days} days</span>
            </div>
            <div className="inline-flex rounded-md border border-[#1A1F2C]/15 overflow-hidden text-xs">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setTrendDays(d)}
                  data-testid={`trend-days-${d}`}
                  className={`px-3 py-1.5 ${trendDays === d ? "bg-[#C85A32] text-white" : "bg-[#FDFBF7] text-[#1A1F2C]"}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend.series} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="tenantsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C85A32" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C85A32" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="ownersFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2B3A67" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2B3A67" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1F2C" strokeOpacity={0.08} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#5C6B7A", fontSize: 11 }}
                  tickFormatter={(d) => d?.slice(5)}
                  minTickGap={20}
                />
                <YAxis tick={{ fill: "#5C6B7A", fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#FDFBF7", border: "1px solid rgba(26,31,44,0.15)", borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: "#1A1F2C" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" name="Tenants" dataKey="tenants" stroke="#C85A32" strokeWidth={2} fill="url(#tenantsFill)" />
                <Area type="monotone" name="Owners" dataKey="owners" stroke="#2B3A67" strokeWidth={2} fill="url(#ownersFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Tabs value={status} onValueChange={setStatus} className="mt-8">
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="admin-tab-rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={status} className="mt-6">
          {items.length === 0 ? (
            <div className="bg-[#F0EBE1] rounded-lg p-10 text-center text-[#5C6B7A]">No {status} verifications.</div>
          ) : (
            <>
            <div className="text-xs text-[#5C6B7A] mb-3" data-testid="admin-pagination-info">
              Showing {skip + 1}–{skip + items.length} of {total}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-verify-list">
              {items.map((v) => (
                <div key={v.id} className="bg-[#F0EBE1] rounded-lg p-5 border border-[#1A1F2C]/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display text-lg">{v.owner_name}</div>
                      <div className="text-xs text-[#5C6B7A]">{v.owner_email}</div>
                      {v.business_name && <div className="text-sm text-[#2B3A67] mt-1">{v.business_name}</div>}
                    </div>
                    <div className="text-xs text-[#5C6B7A]">{new Date(v.submitted_at).toLocaleDateString()}</div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Thumb src={v.aadhaar_photo} label="Aadhaar" />
                    <Thumb src={v.pan_photo} label="PAN" />
                    <Thumb src={v.business_proof} label="Business" />
                  </div>
                  {v.notes && <div className="mt-3 text-xs text-[#5C6B7A]"><b>Notes:</b> {v.notes}</div>}
                  {v.review_notes && <div className="mt-2 text-xs"><b>Reviewed:</b> {v.review_notes}</div>}
                  {v.status === "pending" && (
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" onClick={() => { setSelected({ ...v, action: "approve" }); setNote(""); }} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white" data-testid={`approve-${v.id}`}><CheckCircle2 size={14} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => { setSelected({ ...v, action: "reject" }); setNote(""); }} className="text-red-600" data-testid={`reject-${v.id}`}><XCircle size={14} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {total > items.length + skip || skip > 0 ? (
              <div className="mt-6 flex items-center justify-between gap-4">
                <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="admin-page-prev">Previous</Button>
                <Button variant="outline" size="sm" disabled={skip + items.length >= total} onClick={() => setSkip(skip + LIMIT)} data-testid="admin-page-next">Next</Button>
              </div>
            ) : null}
            </>
          )}
        </TabsContent>
      </Tabs>

      <div id="admin-listings"><AdminListingsPanel /></div>

      <div id="admin-payouts"><PayoutsPanel /></div>

      <DisputesPanel />

      <SupportTicketsPanel />

      <EmailLogPanel />

      <UsersDialog q={usersQ} onClose={() => setUsersQ(null)} />

      <ChatFlagsPanel />

      <UsersPanel />

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.action === "approve" ? "Approve" : "Reject"} verification for {selected?.owner_name}?</DialogTitle>
            <DialogDescription>
              This action updates the owner's is_verified status and notifies them. You can add an optional note visible to the owner.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Optional note visible to owner…" value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="admin-review-note" />
          <DialogFooter>
            <Button onClick={() => decide(selected?.action === "approve")} className={selected?.action === "approve" ? "bg-[#2B3A67] hover:bg-[#1f2c52] text-white" : "bg-red-600 hover:bg-red-700 text-white"} data-testid="admin-review-confirm">
              Confirm {selected?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Thumb({ src, label }) {
  if (!src) return <div className="aspect-square rounded bg-[#FDFBF7] border border-dashed border-[#1A1F2C]/15 flex items-center justify-center text-[10px] text-[#5C6B7A]">{label} — none</div>;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="aspect-square rounded overflow-hidden border border-[#1A1F2C]/10 relative group">
      <img src={src} alt={label} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-[10px] uppercase tracking-[0.18em]"><ExternalLink size={12} className="mr-1" />{label}</div>
    </a>
  );
}

function StatBox({ label, v, sub, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} className="text-left bg-[#F0EBE1] rounded-lg p-4 border border-transparent hover:border-[#C85A32]/50 hover:shadow-md transition-all" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6B7A]">{label}</div>
      <div className="font-display text-3xl text-[#2B3A67] mt-1">{v}</div>
      {sub && <div className="text-xs text-[#5C6B7A] mt-1">{sub} →</div>}
    </button>
  );
}

function UsersDialog({ q, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!q) return;
    setRows(null);
    api.get("/admin/users", { params: q.params }).then(({ data }) => setRows(data)).catch(() => setRows({ items: [], total: 0 }));
  }, [q]);
  return (
    <Dialog open={!!q} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="users-dialog">
        <DialogHeader><DialogTitle>{q?.title}</DialogTitle><DialogDescription>{rows ? `${rows.total} user(s) — live from the database` : "Loading…"}</DialogDescription></DialogHeader>
        {rows && (
          <div className="divide-y divide-[#1A1F2C]/10 text-sm" data-testid="users-dialog-list">
            {rows.items.map((u) => (
              <div key={u.id} className="py-2 flex items-center justify-between gap-3" data-testid={`users-dialog-row-${u.id}`}>
                <div className="min-w-0"><div className="font-medium truncate">{u.name} <span className="text-xs text-[#5C6B7A]">· {u.role}{u.is_verified ? " · verified" : ""}{u.banned ? " · BANNED" : ""}</span></div><div className="text-xs text-[#5C6B7A] truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div></div>
                <div className="text-xs text-[#5C6B7A] shrink-0">{(u.created_at || "").slice(0, 10)}</div>
              </div>
            ))}
            {rows.items.length === 0 && <div className="py-6 text-center text-[#5C6B7A]">No users.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChatFlagsPanel() {
  const [items, setItems] = useState([]);
  const [showReviewed, setShowReviewed] = useState(false);

  const load = () => {
    api.get(`/admin/chat-flags?reviewed=${showReviewed}`).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showReviewed]);

  const mark = async (id) => {
    try {
      await api.post(`/admin/chat-flags/${id}/review`);
      toast.success("Marked reviewed");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <section className="mt-12" data-testid="admin-chat-flags">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Flag size={18} className="text-[#C85A32]" />
          <h2 className="font-display text-2xl">Chat flags · offline-deal detection</h2>
        </div>
        <div className="inline-flex bg-[#F0EBE1] rounded-md p-1 gap-1 text-xs">
          <button onClick={() => setShowReviewed(false)} className={`px-3 py-1.5 rounded ${!showReviewed ? "bg-[#FDFBF7] shadow font-semibold" : "text-[#5C6B7A]"}`} data-testid="flags-tab-open">Open</button>
          <button onClick={() => setShowReviewed(true)} className={`px-3 py-1.5 rounded ${showReviewed ? "bg-[#FDFBF7] shadow font-semibold" : "text-[#5C6B7A]"}`} data-testid="flags-tab-reviewed">Reviewed</button>
        </div>
      </div>
      <p className="text-sm text-[#5C6B7A] mt-2">Messages that mention cash-only, direct phone numbers, or meeting outside the platform are auto-flagged for your review.</p>

      <div className="mt-5 space-y-3">
        {items.length === 0 ? (
          <div className="bg-[#F0EBE1] rounded-lg p-10 text-center text-[#5C6B7A]" data-testid="flags-empty">No {showReviewed ? "reviewed" : "open"} flags.</div>
        ) : items.map((f) => (
          <div key={f.id} className="bg-[#F0EBE1] rounded-lg border border-[#C85A32]/25 p-4" data-testid={`chat-flag-${f.id}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-1 text-[#C85A32] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#5C6B7A]">
                  From <strong className="text-[#1A1F2C]">{f.sender_name}</strong> ({f.sender_role}) · {new Date(f.created_at).toLocaleString()}
                </div>
                <div className="mt-1 text-sm text-[#1A1F2C]">&ldquo;{f.excerpt}&rdquo;</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[#C85A32]">
                  Hits: {(f.hits || []).map((h, i) => <span key={i} className="ml-1">[{h}]</span>)}
                </div>
              </div>
              {!f.reviewed && (
                <Button onClick={() => mark(f.id)} size="sm" variant="outline" className="rounded-md shrink-0" data-testid={`review-flag-${f.id}`}>
                  <CheckCircle2 size={12} className="mr-1" /> Mark reviewed
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


function UsersPanel() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    if (role) p.set("role", role);
    if (q) p.set("q", q);
    const { data } = await api.get(`/admin/users?${p.toString()}`);
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const ban = async (u) => {
    const reason = window.prompt(`Ban "${u.name}" (${u.email}) permanently?\nEnter reason (visible to user on their next login attempt):`);
    if (!reason || reason.trim().length < 3) return;
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/ban`, { reason: reason.trim() });
      toast.success("User banned. Their listings have been deactivated.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };
  const editEmail = async (u) => {
    const next = window.prompt(`Change login email for "${u.name}"?\nCurrent: ${u.email}\n\nEnter the NEW email address (takes effect immediately, no OTP):`, u.email);
    if (next === null) return;
    const clean = next.trim().toLowerCase();
    if (!clean || clean === u.email.toLowerCase()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) return toast.error("Enter a valid email address");
    setBusy(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/email`, { new_email: clean });
      toast.success(`Email updated: ${data.old_email} → ${data.user.email}. Listings, chats & history stay linked.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(null); }
  };
  const unban = async (u) => {
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/unban`);
      toast.success("User reinstated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <section className="mt-12" data-testid="admin-users-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck size={18} className="text-[#2B3A67]" />
        <h2 className="font-display text-2xl">Users · email, ban &amp; reinstate</h2>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap items-center">
        <div className="inline-flex bg-[#F0EBE1] rounded-md p-1 gap-1 text-xs">
          {[["", "All"], ["tenant", "Tenants"], ["owner", "Owners"]].map(([v, label]) => (
            <button key={v} onClick={() => setRole(v)} className={`px-3 py-1.5 rounded ${role === v ? "bg-[#FDFBF7] shadow font-semibold" : "text-[#5C6B7A]"}`}>{label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
          <input
            className="w-full h-9 pl-8 pr-3 rounded-md border border-[#1A1F2C]/15 bg-white text-sm"
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            data-testid="users-search"
          />
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {items.length === 0 ? (
          <div className="bg-[#F0EBE1] rounded-lg p-8 text-center text-[#5C6B7A]">No users match.</div>
        ) : items.map((u) => (
          <div key={u.id} className={`bg-[#F0EBE1] rounded-lg p-3 flex items-center gap-3 flex-wrap ${u.banned ? "border border-red-500/40" : ""}`} data-testid={`admin-user-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {u.name} <span className="text-[#5C6B7A] text-xs">· {u.role}</span>
                {u.banned && <span className="ml-2 text-[10px] uppercase tracking-[0.16em] bg-red-600 text-white px-1.5 py-0.5 rounded">Banned</span>}
              </div>
              <div className="text-xs text-[#5C6B7A]">{u.email}{u.banned_reason ? ` · ${u.banned_reason}` : ""}</div>
            </div>
            <Button size="sm" onClick={() => editEmail(u)} disabled={busy === u.id} variant="outline" className="rounded-md" data-testid={`edit-email-${u.id}`}>
              <Mail size={12} className="mr-1" /> Edit email
            </Button>
            {u.banned ? (
              <Button size="sm" onClick={() => unban(u)} disabled={busy === u.id} variant="outline" className="rounded-md" data-testid={`unban-${u.id}`}>Reinstate</Button>
            ) : (
              <Button size="sm" onClick={() => ban(u)} disabled={busy === u.id} variant="outline" className="rounded-md text-red-600" data-testid={`ban-${u.id}`}>
                <Ban size={12} className="mr-1" /> Ban
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

